import React from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { MdSearchOff, MdLocalOffer, MdSearch } from "react-icons/md";

import styles from "./Promo.module.scss";
import { usePromo } from "./usePromo";
import { useSearch } from "../../common/components/search/useSearch";
import { Search } from "../../common/components/search/Search";
import { PageHeader } from "../../common/components/pageHeader/PageHeader";
import { EmptyState } from "../../common/components/emptyState/EmptyState";
import { selectClusterList } from "../../store/slices/clusterListSlice";
import {
  getClusterImageUrl,
  getFirstImage,
} from "../../common/utils/functions/getImageUri";
import { clusterRoute, promoRoute } from "../../common/utils/constants";

export const Promo: React.FC = () => {
  const clusters = useSelector(selectClusterList);
  const { query, setQuery, clearSearch, isDebouncing } = useSearch(400, true);
  const { items, total, ready, isFetching, isFetchingMore, lastItemRef } =
    usePromo();

  if (!ready) return null;

  const isBusy = isFetching || isDebouncing;

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>Акции — ЛОГМОЛЛ</title>
      </Helmet>

      <PageHeader title="Акции" subtitle={`${total} предложений`} />

      <div className={styles.SearchWrap}>
        <Search
          value={query}
          onChange={setQuery}
          clear={clearSearch}
          placeHolder="Поиск акций и скидок..."
        />
      </div>

      {items.length === 0 && !isBusy ? (
        query.trim() ? (
          <EmptyState
            icon={MdSearchOff}
            title="Ничего не найдено"
            description={`По запросу «${query}» нет акций.`}
          />
        ) : (
          <EmptyState
            icon={MdSearch}
            title="Введите запрос"
            description="Начните вводить название акции, чтобы найти нужное."
          />
        )
      ) : items.length === 0 && isBusy ? (
        <div style={{ minHeight: 400 }} />
      ) : (
        <>
          <div className={styles.Grid}>
            {items.map((promo, index) => {
              const cluster = clusters.find((c) => c.id === promo.clusterId);
              const isLast = index === items.length - 1;
              const image = getFirstImage(promo.files);
              const hasDiscount = promo.discount && promo.discount > 0;

              return (
                <div
                  key={promo.id}
                  className={styles.Card}
                  ref={isLast ? lastItemRef : undefined}
                >
                  <Link to={promoRoute(promo.id)} className={styles.ImageLink}>
                    <div className={styles.ImageWrap}>
                      {image && cluster ? (
                        <img
                          src={getClusterImageUrl(cluster, image)}
                          alt={promo.name}
                          className={styles.Image}
                          loading="lazy"
                        />
                      ) : (
                        <div className={styles.ImagePlaceholder} />
                      )}

                      {hasDiscount && (
                        <div className={styles.DiscountBadge}>
                          -{promo.discount}%
                        </div>
                      )}
                    </div>
                  </Link>

                  <div className={styles.Info}>
                    {cluster && (
                      <Link
                        to={clusterRoute(cluster.id)}
                        className={styles.ClusterLink}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <img
                          src={`https://${cluster.domain}/icons/logo512.png`}
                          alt={cluster.name}
                          className={styles.ClusterLogo}
                        />
                        <span className={styles.ClusterName}>
                          {cluster.name}
                        </span>
                      </Link>
                    )}

                    <Link to={promoRoute(promo.id)} className={styles.NameLink}>
                      <h3 className={styles.Name}>{promo.name}</h3>
                    </Link>

                    {promo.description && (
                      <p className={styles.Description}>{promo.description}</p>
                    )}

                    <div className={styles.Meta}>
                      {promo.datesImportant && (
                        <span className={styles.Dates}>
                          до{" "}
                          {new Date(promo.endDate).toLocaleDateString("ru-RU")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {isFetchingMore && (
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                color: "var(--text-secondary)",
              }}
            >
              Загрузка...
            </div>
          )}
        </>
      )}
    </div>
  );
};
